/*
  bn-play-here · keep every film in the store.

  A shopper who clicks "watch explainer" should not land on youtube.com. This runs store-wide
  and takes over any link that resolves to a single film, in one of two ways:

    1. If the in-store player on this page is already showing that film, it scrolls to it and
       starts it. That is always the better outcome: real page, real context, no overlay.
    2. Otherwise it opens the film in a dialog over the page.

  Nothing is rewritten in the markup. The href stays a real video URL, so with JavaScript off
  the link still works, and a link whose film cannot be identified is left completely alone.
  Explicit data-bn-play-here="<id>" still wins, for a link whose href is not the film itself.

  Deliberately NOT caught: channel, @handle, playlist and user pages. Those are not films, and
  the footer's link to the bionobo channel has to keep going to the channel.

  The in-store player is bn-pdp-video, whose contract is a button carrying
  data-pv-load="<youtube id>". Clicking that button is what swaps the facade for the embed, so
  that is what happens here: no second implementation, no duplicated iframe.
*/
(function () {
  if (window.bnPlayHere) { window.bnPlayHere(); return; }

  var OPT_OUT = 'data-bn-play-away';   /* put this on a link that genuinely must leave */

  function reduceMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* Returns {host, id} for a link that points at one specific film, or null. */
  function filmFrom(link) {
    var explicit = link.getAttribute('data-bn-play-here');
    if (explicit) return { host: 'youtube', id: explicit };
    if (link.hasAttribute(OPT_OUT)) return null;

    var href = link.getAttribute('href') || '';
    if (!/^https?:/i.test(href)) return null;

    var u;
    try { u = new URL(href, window.location.href); } catch (err) { return null; }
    var host = u.hostname.replace(/^www\./, '');
    var path = u.pathname;

    if (host === 'youtube.com' || host === 'm.youtube.com' || host === 'youtube-nocookie.com') {
      /* A watch URL without v= is a channel or a playlist, not a film. */
      var v = u.searchParams.get('v');
      if (path === '/watch' && v) return { host: 'youtube', id: v };
      var m = path.match(/^\/(?:embed|shorts|live|v)\/([\w-]{6,})/);
      if (m) return { host: 'youtube', id: m[1] };
      return null;
    }
    if (host === 'youtu.be') {
      var s = path.match(/^\/([\w-]{6,})/);
      return s ? { host: 'youtube', id: s[1] } : null;
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      var vm = path.match(/(?:^|\/)(\d{6,})/);
      return vm ? { host: 'vimeo', id: vm[1] } : null;
    }
    return null;
  }

  function embedSrc(film) {
    if (film.host === 'vimeo') {
      return 'https://player.vimeo.com/video/' + encodeURIComponent(film.id) + '?autoplay=1&title=0&byline=0';
    }
    /* nocookie, and rel=0 so the end card offers this channel rather than the whole of YouTube. */
    return 'https://www.youtube-nocookie.com/embed/' + encodeURIComponent(film.id) + '?autoplay=1&rel=0&modestbranding=1&playsinline=1';
  }

  /* ---- the dialog, built once and reused ---- */
  var dlg = null;
  var lastFocus = null;

  function buildDialog() {
    if (dlg) return dlg;
    dlg = document.createElement('div');
    dlg.className = 'bn-filmbox';
    dlg.setAttribute('role', 'dialog');
    dlg.setAttribute('aria-modal', 'true');
    dlg.setAttribute('aria-label', window.BionoboI18n.s_7f5a092413f05d73cb7a2e12);
    dlg.hidden = true;
    dlg.innerHTML =
      '<div class="bn-filmbox__scrim" data-filmbox-close></div>' +
      '<div class="bn-filmbox__panel">' +
      '<button type="button" class="bn-filmbox__x" data-filmbox-close aria-label="' + window.BionoboI18n.s_939b080f009d053675cc985e + '">' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>' +
      '</button>' +
      '<div class="bn-filmbox__frame" data-filmbox-frame></div>' +
      '</div>';
    document.body.appendChild(dlg);

    dlg.addEventListener('click', function (e) {
      if (e.target.closest('[data-filmbox-close]')) close();
    });
    document.addEventListener('keydown', function (e) {
      if (dlg.hidden) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      /* Two focusables at most: the close button and the iframe. Keep Tab inside. */
      var f = dlg.querySelectorAll('button, iframe');
      if (!f.length) return;
      var first = f[0];
      var last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
    return dlg;
  }

  function open(film, label) {
    buildDialog();
    lastFocus = document.activeElement;
    dlg.setAttribute('aria-label', label || window.BionoboI18n.s_67bbf819d2e36452657c06c2);
    var frame = dlg.querySelector('[data-filmbox-frame]');
    var iframe = document.createElement('iframe');
    iframe.src = embedSrc(film);
    iframe.title = label || window.BionoboI18n.s_3946ae800d3dc8365a657cce;
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.allowFullscreen = true;
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    frame.appendChild(iframe);
    dlg.hidden = false;
    document.documentElement.classList.add('bn-filmbox-open');
    dlg.querySelector('.bn-filmbox__x').focus();
  }

  function close() {
    if (!dlg || dlg.hidden) return;
    /* Emptying the frame is what stops the sound. Hiding the dialog would not. */
    dlg.querySelector('[data-filmbox-frame]').innerHTML = '';
    dlg.hidden = true;
    document.documentElement.classList.remove('bn-filmbox-open');
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onClick(e) {
    /* Let a modified click do what the shopper asked for: new tab, download, context menu. */
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    var link = e.target.closest('a[href], [data-bn-play-here]');
    if (!link) return;

    var film = filmFrom(link);
    if (!film) return;

    var target = film.host === 'youtube' ? document.querySelector('[data-pv-load="' + film.id + '"]') : null;

    e.preventDefault();

    if (target) {
      var stage = target.closest('[data-bn-pv]') || target;
      stage.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'center' });
      /* Start it once the scroll has settled, so the film does not begin playing off screen. */
      window.setTimeout(function () { target.click(); }, reduceMotion() ? 0 : 420);
      return;
    }

    open(film, (link.textContent || '').trim() || window.BionoboI18n.s_8bab7bada2f9c2e76242236a);
  }

  /* The init flag is deliberately a different attribute from the one on the links. Naming it
     the same put data-bn-play-here on <html>, which then matched every selector looking for a
     link. */
  window.bnPlayHere = function () {
    if (document.documentElement.dataset.bnPlayHereInit) return;
    document.documentElement.dataset.bnPlayHereInit = '1';
    document.addEventListener('click', onClick);
  };

  window.bnPlayHere();
}());
